import React from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import {
  getDateRangeString,
  getTimeFromDateString,
} from "../../../libs/dateFormat";

import {
  GetStepFromReservationStatus,
  ReservationStatus,
} from "../../../services/spaceReservationServices";
import { HistoryItem } from "../../../services/historyServices";

import VerticalRule from "../../Rule/Vertical";
import HorizontalRule from "../../Rule/Horizontal";
import ProgressBar from "../../ProgressBar";
import { CancelCompleteBadge } from "../IncommodityItem";

import { ReportStatusRadioIcon } from "../../../pages/Manager/Report";

type Props = { index: number; item: HistoryItem; items: HistoryItem[] };

export default function HistoryReservationItem({ index, item, items }: Props) {
  const navigate = useNavigate();

  const getReservationStatus = (statusName: string) => {
    switch (statusName) {
      case "예약 취소":
        return "CANCELED";
      case "사용 완료":
      case "정리 중":
        return "COMPLETED";
      case "사용 중":
      case "예약 완료":
      case "승인 완료":
        return "PROCESSED";
      case "예약 대기":
      default:
        return "DEFAULT";
    }
  };

  return (
    <React.Fragment key={index}>
      <div className="flex gap-2.5 relative">
        <div className="h-full w-[24px] flex justify-center absolute z-0">
          {items.length > 1 && (
            <VerticalRule
              className={clsx([
                "!h-full !bg-[#d9d9d9]",
                {
                  "!h-[calc(100%-24px)] ": index === 0,
                  "!h-[24px]": index === items.length - 1,
                },
              ])}
              wrapperClassName={clsx([
                {
                  "!items-end": index === 0,
                  "!items-start": index === items.length - 1,
                },
              ])}
            />
          )}
        </div>
        <div className="w-[24px] pt-[18px] z-10">
          <ReportStatusRadioIcon
            status={getReservationStatus(item.statusName)}
          />
        </div>
        <div className="flex flex-col gap-1 pt-[18px]">
          <div className="text-base font-bold">
            {getTimeFromDateString(item.createdAt)}
          </div>
          <div className=" text-xs text-[#9f9f9f]">
            {item.createdAt?.split(" ")[0]}
          </div>
          {item.statusName === ReservationStatus.CANCELED && (
            <CancelCompleteBadge />
          )}
        </div>
        <div
          className={clsx([
            "p-[15px] border border-brand-neutral-10 rounded-[10px] flex flex-col gap-2.5 flex-1 h-fit",
            {
              "bg-brand-neutral-05":
                item.statusName === ReservationStatus.CANCELED.toString(),
            },
          ])}
          onClick={() => navigate(`/history/${item.type}/${item.serviceId}`)}
        >
          <div className="flex gap-2.5">
            <HistoryReservationItemIcon />
            <div className="flex flex-col gap-0.5">
              <div
                className={clsx([
                  {
                    " text-brand-neutral-50":
                      item.statusName === ReservationStatus.CANCELED.toString(),
                  },
                ])}
              >
                {item.lowerCategoryName}
              </div>
              <div className="text-xs text-brand-neutral-50">
                {item.midCategoryName}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            <HorizontalRule margin="0" />
            <div className="text-xs text-brand-neutral-50 flex flex-col gap-1.5">
              <div className="flex gap-1 items-center">
                <HistoryReservationLocationIcon />
                {item.buildingName} {item.floorName}
              </div>
              {item.startDate && item.endDate && (
                <div className="flex gap-1 items-center">
                  <HistoryReservationCalendarIcon />
                  {getDateRangeString(item.startDate, item.endDate)}
                </div>
              )}
              <div className="flex gap-1 items-center">
                <HistoryReservationPersonIcon />
                {item.usedDepartment} {item.actualUserName}
              </div>
            </div>
            {item.statusName !== ReservationStatus.CANCELED.toString() && (
              <div className="flex flex-col gap-1.5">
                <div className=" bg-brand-primary-magenta bg-clip-text text-transparent font-semibold w-fit text-xs">
                  {item?.statusName}
                </div>
                <ProgressBar
                  currentStep={GetStepFromReservationStatus(item.statusName)}
                  wrapperClassName="!p-0"
                  className="h-2.5 !bg-brand-primary-magenta"
                  rounded
                  nonSticky
                  totalStep={4}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {index < items.length - 1 && (
        <div className="h-5 w-[24px] flex justify-center">
          <VerticalRule className="!h-full !bg-[#d9d9d9]" />
        </div>
      )}
    </React.Fragment>
  );
}

export const HistoryReservationPersonIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.5731 4.626C10.5731 6.05808 9.4116 7.21879 7.97858 7.21879C6.54556 7.21879 5.38409 6.05808 5.38409 4.626C5.38409 3.19391 6.54556 2.0332 7.97858 2.0332C9.4116 2.0332 10.5731 3.19391 10.5731 4.626Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.8143 13.2916H3.14282C3.14282 13.2623 3.14282 13.4496 3.14282 12.858C3.14282 11.2556 4.44185 9.95703 6.04427 9.95703H9.91287C11.5153 9.95703 12.8143 11.2556 12.8143 12.858C12.8143 13.4496 12.8143 13.2623 12.8143 13.2916Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
  </svg>
);

export const HistoryReservationCalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="2.49988"
      y="3.32227"
      width="11"
      height="10"
      rx="1"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.23"
    />
    <line
      x1="5.61488"
      y1="1.32227"
      x2="5.61488"
      y2="5.32227"
      stroke="white"
      strokeWidth="1.23"
    />
    <line
      x1="10.6149"
      y1="1.32227"
      x2="10.6149"
      y2="5.32227"
      stroke="white"
      strokeWidth="1.23"
    />
    <line
      x1="5.61488"
      y1="2.59203e-08"
      x2="5.61488"
      y2="3"
      stroke="#B7B7B8"
      strokeWidth="1.23"
    />
    <line
      x1="10.6149"
      y1="2.59203e-08"
      x2="10.6149"
      y2="3"
      stroke="#B7B7B8"
      strokeWidth="1.23"
    />
    <line x1="2.99988" y1="7.02246" x2="12.9999" y2="7.02246" stroke="white" />
  </svg>
);

export const HistoryReservationLocationIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.97849 14.0273L4.13514 10.3126C3.39363 9.58116 2.89205 8.65438 2.69331 7.64849C2.49457 6.64261 2.6075 5.6024 3.01795 4.6583C3.42841 3.71421 4.1181 2.90825 5.00057 2.34148C5.88303 1.77472 6.91897 1.47238 7.9785 1.47238C9.03802 1.47238 10.074 1.77472 10.9564 2.34148C11.8389 2.90825 12.5286 3.71421 12.939 4.65831C13.3495 5.6024 13.4624 6.64261 13.2637 7.64849C13.0649 8.65438 12.5634 9.58116 11.8218 10.3126L7.97849 14.0273Z"
      fill="#3E3E40"
      stroke="#3E3E40"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.1744 6.93431C10.1744 8.14714 9.19121 9.13033 7.97837 9.13033C6.76554 9.13033 5.78235 8.14714 5.78235 6.93431C5.78235 5.72148 6.76554 4.73828 7.97837 4.73828C9.19121 4.73828 10.1744 5.72148 10.1744 6.93431Z"
      fill="white"
      stroke="white"
      strokeWidth="1.75"
    />
  </svg>
);

export const HistoryReservationItemIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="39"
      height="39"
      rx="3.5"
      fill="white"
      stroke="#CFCFCF"
    />
    <path
      d="M32.768 12.0361H6.73001C6.49427 12.0361 6.30316 12.2493 6.30316 12.5123V31.5599C6.30316 31.8229 6.49427 32.0361 6.73001 32.0361H32.768C33.0038 32.0361 33.1949 31.8229 33.1949 31.5599V12.5123C33.1949 12.2493 33.0038 12.0361 32.768 12.0361Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.0494 14.2173V9.64994C15.0494 8.72367 15.8092 7.96387 16.7354 7.96387H18.4215"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M22.3912 14.2173V9.64994C22.3912 8.72367 23.151 7.96387 24.0773 7.96387H25.7634"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M6.30316 16.5176H28.9178"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M21.265 24.9815L24.2927 28.0039L28.9178 23.3789"
      stroke="url(#paint0_linear_1398_38246)"
      strokeWidth="2"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1398_38246"
        x1="28.9178"
        y1="23.3789"
        x2="21.5641"
        y2="26.5106"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E100A3" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);
